import { type FormEvent, useCallback, useMemo, useState } from 'react';
import { mapFileFormatApiError } from '@/lib/file-format-errors';
import type { FileFormatFormFieldErrors, FileFormatFormValues } from './file-format-form.types';
import { validateFileFormatForm } from './file-format-form.utils';

interface UseFileFormatSubmitArgs {
  /** Current form values to validate and submit. */
  values: FileFormatFormValues;
  /** Triggers JSON-editor validation; should return false to abort the submit. */
  validateJsonEditors: () => boolean;
  /** Performs the API call (create / update). May throw; caller does not need to map errors. */
  perform: (values: FileFormatFormValues) => Promise<unknown>;
  /** Called once after a successful submit (e.g. close modal + refetch list). */
  onSuccess: () => void;
}

interface UseFileFormatSubmitReturn {
  isLoading: boolean;
  fieldErrors: FileFormatFormFieldErrors;
  /**
   * Combined reactive validity: Zod schema over `values` AND every JSON
   * editor's live state. False whenever the shared `fileFormatFormSchema`
   * rejects the values (e.g. empty required fields, party mismatch for
   * the chosen scope) OR any JSON editor is reporting invalid content via
   * `reportJsonValidity`. Consumers gate their submit button on this.
   */
  isValid: boolean;
  /**
   * Wire this into `<FileFormatForm onJsonValidityChange={...} />` so the
   * hook learns about live JSON-editor state and folds it into `isValid`.
   */
  reportJsonValidity: (allValid: boolean) => void;
  /** Submit handler safe to bind to a `<form onSubmit>` directly. */
  handleSubmit: (e: FormEvent) => Promise<void>;
  /** Clears errors + loading; intended for modal close/reset effects. */
  reset: () => void;
}

/**
 * Centralizes the create/update submission flow for file-format modals.
 *
 * Responsibilities (single concern: orchestrating a validated submit):
 * - Run client-side Zod validation against the shared form schema.
 * - Defer to the form's `validateJsonEditors` for JSONB blob validation.
 * - Invoke the caller-provided `perform` and translate failures into
 *   field-level errors via `mapFileFormatApiError`.
 *
 * Modals supply only the differing pieces (the API call + success handler),
 * keeping them free of duplicated submit/loading/error wiring.
 */
export const useFileFormatSubmit = ({
  values,
  validateJsonEditors,
  perform,
  onSuccess,
}: UseFileFormatSubmitArgs): UseFileFormatSubmitReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FileFormatFormFieldErrors>({});
  const [jsonValid, setJsonValid] = useState(true);

  // Single owner of "what is the current Zod state of this form": both
  // the reactive `isValid` gate and the submit handler read from this
  // memo, so the schema is parsed once per `values` change instead of
  // twice (one for the button-disable, one for the submit guard).
  const validation = useMemo<{ errors: FileFormatFormFieldErrors | null; valid: boolean }>(() => {
    const errors = validateFileFormatForm(values);
    return { errors, valid: errors === null };
  }, [values]);

  const isValid = validation.valid && jsonValid;

  const reportJsonValidity = useCallback((allValid: boolean) => {
    setJsonValid((prev) => (prev === allValid ? prev : allValid));
  }, []);

  const reset = useCallback(() => {
    setFieldErrors({});
    setIsLoading(false);
    setJsonValid(true);
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setFieldErrors({});

      if (validation.errors) {
        setFieldErrors(validation.errors);
        return;
      }

      if (!validateJsonEditors()) return;

      setIsLoading(true);
      try {
        await perform(values);
        onSuccess();
      } catch (err) {
        const mapped = mapFileFormatApiError(err);
        setFieldErrors({ [mapped.field]: mapped.message });
      } finally {
        setIsLoading(false);
      }
    },
    [values, validation, validateJsonEditors, perform, onSuccess]
  );

  return { isLoading, fieldErrors, isValid, reportJsonValidity, handleSubmit, reset };
};
